'use client'

import { useState, useMemo, useRef, useEffect } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faSpinner,
  faExclamationTriangle,
  faRefresh,
  faSquare,
  faChevronLeft,
  faChevronRight,
  faCalendar,
  faBell,
  faCalendarPlus,
  faPlay
} from '@fortawesome/free-solid-svg-icons'
import Image from 'next/image'
import { useAllMatches } from '@/hooks/useAllMatches'
import { getStatusText, isLiveMatch, type FootballMatch } from '@/lib/football-api'
import '@/app/lich-thi-dau-bong-da/lich-thi-dau.css'

// Format match date/time
function formatMatchDateTime(date: string, time: string | undefined | null): string {
  if (!time || typeof time !== 'string') {
    return '--:--' // Default fallback
  }
  const timeStr = time.slice(0, 5) // Chỉ lấy HH:MM
  return timeStr
}

interface LeagueGroup {
  id: number
  name: string
  logo: string
  matches: FootballMatch[]
}

export default function LichThiDau() {
  const [selectedDate, setSelectedDate] = useState<string>(
    new Date().toISOString().split('T')[0]
  )
  const [activeLeague, setActiveLeague] = useState('all')
  const [lastSelectedLeague, setLastSelectedLeague] = useState<string | null>(null)
  const [isMobile, setIsMobile] = useState(false)

  const leaguesRef = useRef<HTMLDivElement>(null)

  // Check if mobile
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768)
    }

    checkMobile()
    window.addEventListener('resize', checkMobile)

    return () => window.removeEventListener('resize', checkMobile)
  }, [])

  // Use the matches hook - always fetch 'all' data
  const { allMatches, loading, error, refetch } = useAllMatches({
    date: selectedDate,
    league: 'all', // Always fetch all data
    autoRefresh: true,
    refreshInterval: 300000 // 5 minutes
  })

  // Filter matches based on active league (client-side)
  const filteredMatches = useMemo(() => {
    // First filter out matches that already have results (chỉ lấy trận chưa có kết quả)
    const upcomingMatches = allMatches.filter((match: FootballMatch) =>
      match.home_score === null && match.away_score === null
    )

    if (activeLeague === 'all') {
      return upcomingMatches
    }
    return upcomingMatches.filter((match: FootballMatch) =>
      match.competition_id.toString() === activeLeague
    )
  }, [allMatches, activeLeague])

  // Generate dates for calendar navigation
  const calendarDates = useMemo(() => {
    const dates = []
    const today = new Date()

    // 3 days before today
    for (let i = 3; i >= 1; i--) {
      const date = new Date(today)
      date.setDate(today.getDate() - i)
      dates.push(date)
    }

    // Today
    dates.push(today)

    // 7 days after today
    for (let i = 1; i <= 7; i++) {
      const date = new Date(today)
      date.setDate(today.getDate() + i)
      dates.push(date)
    }

    return dates
  }, [])

  // Handle date navigation
  const navigateDate = (direction: 'prev' | 'next') => {
    const currentDate = new Date(selectedDate)
    if (direction === 'prev') {
      currentDate.setDate(currentDate.getDate() - 1)
    } else {
      currentDate.setDate(currentDate.getDate() + 1)
    }
    setSelectedDate(currentDate.toISOString().split('T')[0])
  }

  // Handle wheel scroll for horizontal scrolling
  const handleWheelScroll = (e: React.WheelEvent<HTMLDivElement>) => {
    const target = e.currentTarget
    if (target && e.deltaY !== 0 && target.scrollWidth > target.clientWidth) {
      e.preventDefault()
      target.scrollLeft += e.deltaY * 0.5
    }
  }

  // Get all available leagues from matches
  const leagues = useMemo(() => {
    const leagueMap = new Map()

    // Filter to only upcoming matches first (chỉ tính trận chưa có kết quả)
    const upcomingMatches = allMatches.filter((match: FootballMatch) =>
      match.home_score === null && match.away_score === null
    )

    upcomingMatches.forEach((match: FootballMatch) => {
      if (!leagueMap.has(match.competition_id)) {
        leagueMap.set(match.competition_id, {
          id: match.competition_id,
          name: match.competition_name,
          logo: match.competition_emblem,
          count: 0
        })
      }
      leagueMap.get(match.competition_id).count++
    })

    const allLeagues = Array.from(leagueMap.values())
    const totalUpcomingMatches = upcomingMatches.length

    const leaguesList = [
      {
        id: 'all',
        name: 'Tất cả',
        logo: '',
        count: totalUpcomingMatches,
        priority: 0
      },
      ...allLeagues
    ]

    // Sort leagues: active league first (if not 'all'), then by count
    return leaguesList.sort((a: any, b: any) => {
      // Nếu có active league và không phải 'all'
      if (activeLeague !== 'all') {
        if (a.id.toString() === activeLeague && b.id.toString() !== activeLeague) {
          return -1 // a lên đầu
        }
        if (b.id.toString() === activeLeague && a.id.toString() !== activeLeague) {
          return 1 // b lên đầu
        }
      }

      // Giữ 'Tất cả' luôn ở đầu
      if (a.id === 'all') return -1
      if (b.id === 'all') return 1

      // Sort theo số trận
      return b.count - a.count
    })
  }, [allMatches, activeLeague])

  // Group matches by league using filtered data
  const groupedMatches = useMemo(() => {
    if (activeLeague === 'all') {
      // Group by league (chỉ những trận chưa có kết quả)
      const groups = filteredMatches.reduce((acc: Record<string, LeagueGroup>, match: FootballMatch) => {
        const key = match.competition_id.toString()
        if (!acc[key]) {
          acc[key] = {
            id: match.competition_id,
            name: match.competition_name,
            logo: match.competition_emblem,
            matches: []
          }
        }
        acc[key].matches.push(match)
        return acc
      }, {})

      const groupsArray = Object.values(groups)

      // Sort with priority: last selected league first, then by match count
      return groupsArray.sort((a, b) => {
        if (lastSelectedLeague) {
          if (a.id.toString() === lastSelectedLeague && b.id.toString() !== lastSelectedLeague) {
            return -1 // a lên đầu
          }
          if (b.id.toString() === lastSelectedLeague && a.id.toString() !== lastSelectedLeague) {
            return 1 // b lên đầu
          }
        }
        // Nếu không có priority hoặc cả hai đều không phải last selected, sort theo số trận
        return b.matches.length - a.matches.length
      })
    } else {
      // Show single league with filtered data (chỉ những trận chưa có kết quả)
      if (filteredMatches.length === 0) return []

      const firstMatch = filteredMatches[0]
      return [{
        id: firstMatch.competition_id,
        name: firstMatch.competition_name,
        logo: firstMatch.competition_emblem,
        matches: filteredMatches
      }]
    }
  }, [filteredMatches, activeLeague, lastSelectedLeague])

  const handleLeagueChange = (leagueId: string | number) => {
    const leagueIdStr = leagueId.toString()
    setActiveLeague(leagueIdStr)

    // Lưu lại giải đấu được chọn (trừ 'all')
    if (leagueIdStr !== 'all') {
      setLastSelectedLeague(leagueIdStr)
    }
  }

  const formatDateForCalendar = (date: Date) => {
    const today = new Date()
    const isToday = date.toDateString() === today.toDateString()

    return {
      weekday: date.toLocaleDateString('vi-VN', { weekday: 'short' }).toUpperCase(),
      day: date.getDate(),
      month: date.toLocaleDateString('vi-VN', { month: 'short' }),
      isToday,
      dateString: date.toISOString().split('T')[0]
    }
  }

  return (
    <div className="match-results">
      {/* Calendar Navigation */}
      <div className="calendar-nav">
        <button
          className="calendar-nav__arrow"
          onClick={() => navigateDate('prev')}
        >
          <FontAwesomeIcon icon={faChevronLeft} />
        </button>

        <div className="calendar-nav__dates">
          {calendarDates.map((date, index) => {
            const { weekday, day, month, isToday, dateString } = formatDateForCalendar(date)
            const isActive = selectedDate === dateString

            return (
              <button
                key={index}
                className={`calendar-nav__date ${isActive ? 'calendar-nav__date--active' : ''} ${isToday ? 'calendar-nav__date--today' : ''}`}
                onClick={() => setSelectedDate(dateString)}
              >
                {isToday && <div className="calendar-nav__today-mark">Hôm nay</div>}
                <div className="calendar-nav__weekday">{weekday}</div>
                <div className="calendar-nav__day">{day}</div>
                <div className="calendar-nav__month">{month}</div>
              </button>
            )
          })}
        </div>

        <button
          className="calendar-nav__arrow"
          onClick={() => navigateDate('next')}
        >
          <FontAwesomeIcon icon={faChevronRight} />
        </button>

        <button
          className="calendar-nav__today-btn"
          onClick={() => setSelectedDate(new Date().toISOString().split('T')[0])}
          title="Về hôm nay"
        >
          <FontAwesomeIcon icon={faCalendar} />
        </button>
      </div>

      {/* League Filters */}
      <div className="league-filters" ref={leaguesRef} onWheel={handleWheelScroll}>
        {leagues.map((league) => (
          <button
            key={league.id}
            className={`league-filters__button ${activeLeague === league.id.toString() ? 'league-filters__button--active' : ''
              }`}
            onClick={() => handleLeagueChange(league.id)}
          >
            {league.logo && (
              <Image
                src={league.logo}
                alt={league.name}
                width={20}
                height={20}
                onError={(e) => {
                  e.currentTarget.style.display = 'none'
                }}
              />
            )}
            <span className="league-name">{league.name}</span>
            <span>{league.count}</span>
          </button>
        ))}
      </div>

      {/* Matches Container */}
      <div className="results-grid">
        {/* Loading State */}
        {loading && (
          <div className="loading-state">
            <FontAwesomeIcon icon={faSpinner} spin size="2x" />
            <p>Đang tải lịch thi đấu...</p>
          </div>
        )}

        {/* Error State */}
        {error && (
          <div className="error-state">
            <FontAwesomeIcon icon={faExclamationTriangle} size="2x" />
            <div>
              <p>Có lỗi xảy ra: {error}</p>
              <button className="retry-btn" onClick={refetch}>
                <FontAwesomeIcon icon={faRefresh} />
                Thử lại
              </button>
            </div>
          </div>
        )}

        {/* No Results */}
        {!loading && !error && groupedMatches.length === 0 && (
          <div className="no-results">
            <FontAwesomeIcon icon={faSquare} size="2x" />
            <p>Không có trận đấu nào trong ngày này</p>
            <div className="no-results-suggestion">
              Thử chọn ngày khác hoặc giải đấu khác
            </div>
          </div>
        )}

        {/* Display matches grouped by league */}
        {groupedMatches.map((league: LeagueGroup) => (
          <section key={league.id} className="results-section">
            <div className="league-schedule">
              <div className="league-header">
                <Image
                  src={league.logo}
                  alt={league.name}
                  width={32}
                  height={32}
                  onError={(e) => {
                    e.currentTarget.style.display = 'none'
                  }}
                />
                <h2>{league.name}</h2>
                <span className="match-count">{league.matches.length} trận</span>
              </div>

              {/* Table Format for Schedule */}
              <div className="schedule-table-container">
                {/* Table Header */}
                <div className="schedule-table-header">
                  <div className="schedule-col schedule-col-time">Giờ</div>
                  <div className="schedule-col schedule-col-home">Chủ nhà</div>
                  <div className="schedule-col schedule-col-score">VS</div>
                  <div className="schedule-col schedule-col-away">Khách</div>
                  <div className="schedule-col schedule-col-stadium">{isMobile ? 'Kênh' : 'Kênh phát sóng'}</div>
                  <div className="schedule-col schedule-col-actions">Thao tác</div>
                </div>

                {/* Table Body */}
                <div className="schedule-table-body">
                  {league.matches.map((match) => (
                    <div key={match.match_id} className="schedule-table-row schedule-row">
                      <div className="schedule-col schedule-col-time">
                        <div className="match-time-info">
                          {isLiveMatch(match.status) ? (
                            <span className="live-status">LIVE</span>
                          ) : (
                            <span className="match-time">
                              {formatMatchDateTime(match.match_date, match.match_time)}
                            </span>
                          )}
                        </div>
                      </div>

                      <div className="schedule-col schedule-col-home">
                        <span className="schedule-team-name">{match.home_team_name}</span>
                        <Image
                          src={match.home_team_crest}
                          alt={match.home_team_name}
                          width={20}
                          height={20}
                          onError={(e) => {
                            e.currentTarget.style.display = 'none'
                          }}
                        />
                      </div>

                      <div className="schedule-col schedule-col-score">
                        {match.home_score !== null && match.away_score !== null ? (
                          <div className="live-score">
                            {match.home_score}-{match.away_score}
                          </div>
                        ) : (
                          <div className="vs-text">VS</div>
                        )}
                      </div>

                      <div className="schedule-col schedule-col-away">
                        <Image
                          src={match.away_team_crest}
                          alt={match.away_team_name}
                          width={20}
                          height={20}
                          onError={(e) => {
                            e.currentTarget.style.display = 'none'
                          }}
                        />
                        <span className="schedule-team-name">{match.away_team_name}</span>
                      </div>

                      <div className="schedule-col schedule-col-stadium">
                        <div className="stadium-info">
                          <div className="channels">
                            <span className="channel-tag">
                              <FontAwesomeIcon icon={faPlay} />
                              Xoilac TV
                            </span>
                          </div>
                        </div>
                      </div>

                      <div className="schedule-col schedule-col-actions">
                        <div className="match-actions">
                          <button className="action-btn notify-btn" title="Nhận thông báo">
                            <FontAwesomeIcon icon={faBell} />
                          </button>
                          <button className="action-btn calendar-btn" title="Thêm vào lịch">
                            <FontAwesomeIcon icon={faCalendarPlus} />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </section>
        ))}

        {/* <div className="results-footer">
          <p>
            Hiển thị {filteredMatches.length} trận đấu ngày {new Date(selectedDate).toLocaleDateString('vi-VN')}
          </p>
          <p>Dữ liệu cập nhật mỗi 5 phút</p>
        </div> */}
      </div>
    </div>
  )
} 